import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HttpClient } from '@angular/common/http';
import { formatDisplayText } from '../../utils/text-case.utils';
import { Router } from '@angular/router';
import { executeNavigationFromConfig } from '../../shared-services/chart-wrapper.service';

@Component({
  selector: 'app-button',
  standalone: true,
  imports: [MatButtonModule, CommonModule, MatIconModule, MatTooltipModule],
  templateUrl: './button.component.html',
  styleUrl: './button.component.scss'
})
export class ButtonComponent implements OnInit {
  @Input() data!: any;
  @Input() selectedKey!: string;
  @Input() showResetButton = false;
  @Output() selectionChange = new EventEmitter<string>();
  @Output() resetClick = new EventEmitter<void>();
  tooltipText = 'Insights';
  private readonly defaultInsightUrl = 'assets/tooltip-hover/tooltip-hover.json';

  constructor(
    private globalFilterService: GlobalFilterService,
    private http: HttpClient,
    private router: Router,
  ) {}

  get showInsightButton(): boolean {
    if (this.data?.key !== 'insights') {
      return false;
    }
    return this.data?.enableInsight !== false;
  }

  get isResetVisible(): boolean {
    if (this.data?.showReset === true) return true;
    if (this.data?.showReset === false) return false;
    return this.showResetButton;
  }

  get isInsightVisible(): boolean {
    if (this.data?.showInsight === true) return true;
    if (this.data?.showInsight === false) return false;
    return this.showInsightButton;
  }

  get showMainButton(): boolean {
    return this.data?.iconOnly !== true;
  }

  /**
   * Per UI Standards (Point 3): button label in Capital Case unless overridden.
   */
  get displayLabel(): string {
    const mode = (this.data?.textCase || 'capital') as any;
    return formatDisplayText(this.data?.label, mode);
  }

  get insightTooltipText(): string {
    return this.tooltipText;
  }

  ngOnInit(): void {
    if (!this.showInsightButton) {
      return;
    }

    if (this.data?.insightTooltip) {
      this.tooltipText = this.data.insightTooltip;
      return;
    }

    const insightApiUrl =
      typeof this.data?.insightApiUrl === 'string' && this.data.insightApiUrl.trim().length > 0
        ? this.data.insightApiUrl
        : this.defaultInsightUrl;
    this.http.get<any>(insightApiUrl).subscribe({
      next: (response) => {
        this.tooltipText = this.buildTooltipText(response);
      },
      error: () => {
        this.tooltipText = 'Insights';
      },
    });
  }

  onClick(value: string) {
    if (!value) {
      return;
    }

    this.selectionChange.emit(value); // Notify parent

    // Insights button is informational only and should not mutate global filters.
    if (this.data?.key === 'insights') {
      return;
    }

    const filterKey = this.data?.filterKey || this.data?.key || this.data?.type;
    if (this.data && filterKey) {
      this.globalFilterService.updateFilterSelection(
        filterKey,
        [value],
        this.data.isQueryParam
      );
    }

    executeNavigationFromConfig(this.router, this.data, { name: value, value });
  }

  isSelected(): boolean {
    return this.selectedKey === this.data.key;
  }

  onResetClick(event: MouseEvent): void {
    event.stopPropagation();
    this.resetClick.emit();
  }

  private buildTooltipText(response: any): string {
    const insights = response?.insights;
    if (!Array.isArray(insights) || insights.length === 0) {
      return 'Insights';
    }

    const formattedText = insights
      .map((item: any) => {
        const period = item?.period || '';
        const points = Array.isArray(item?.points)
          ? item.points.map((point: string) => `• ${point}`).join('\n')
          : '';
        return [period, points].filter(Boolean).join('\n');
      })
      .join('\n\n');

    return formattedText || 'Insights';
  }
}
